import "@/App.css";
import PublicWebsiteEvents from "@/components/PublicWebsiteEvents";
import PageErrorBoundary from "@/components/PageErrorBoundary";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Layout } from "@/components/Layout";
import HoldingsHome from "@/pages/HoldingsHome";
import About from "@/pages/Home";
import Portfolio from "@/pages/Portfolio";

// Steward & Stone — strategic & operational advisory
import StewardStone from "@/pages/StewardStone";
import HealthcareAdvisory from "@/pages/HealthcareAdvisory";
import OperationalStrategy from "@/pages/OperationalStrategy";
import StewardAssessment from "@/pages/StewardAssessment";

// Strategic Assets — JMH portfolio company
import StrategicAssets from "@/pages/StrategicAssets";
import SAIntelligence from "@/pages/SAIntelligence";
import SATaxOptimization from "@/pages/SATaxOptimization";
import SARecovery from "@/pages/SARecovery";
import SAAcquisitions from "@/pages/SAAcquisitions";
import SARealEstate from "@/pages/SARealEstate";

import Foundation from "@/pages/Foundation";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import InvestorLogin from "@/pages/InvestorLogin";
import FounderInbox from "@/pages/FounderInbox";
import SecureIntake from "@/pages/SecureIntake";
import FounderCloseConsole from "@/pages/FounderCloseConsole";
import FounderTimeLedger from "@/pages/FounderTimeLedger";
import FounderPlanningConsole from "@/pages/FounderPlanningConsole";
import FounderAssetRegister from "@/pages/FounderAssetRegister";
import FounderProcureToPay from "@/pages/FounderProcureToPay";
import FounderContractToCash from "@/pages/FounderContractToCash";
import FounderTreasury from "@/pages/FounderTreasury";
import FounderTaxCompliance from "@/pages/FounderTaxCompliance";
import FounderGovernance from "@/pages/FounderGovernance";
import FounderCFO from "@/pages/FounderCFO";
import FounderHoursSheet from "@/pages/FounderHoursSheet";
import FounderOperations from "@/pages/FounderOperations";
import FounderProfile from "@/pages/FounderProfile";
import FounderOwnership from "@/pages/FounderOwnership";
import { FounderAccess, FounderLogin } from "@/components/FounderAccess";
import LegalPortal from "@/pages/LegalPortal";
import WebsiteAnalytics from "@/pages/WebsiteAnalytics";
import NotFound from "@/pages/NotFound";

function App() {
  return (
    <BrowserRouter>
      <Toaster theme="dark" position="top-center" richColors />
      <PublicWebsiteEvents />
      <PageErrorBoundary><Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<HoldingsHome />} />
          <Route path="/about" element={<About />} />
          <Route path="/portfolio" element={<Portfolio />} />

          {/* Steward & Stone — strategic & operational advisory */}
          <Route path="/steward-and-stone" element={<StewardStone />} />
          <Route path="/steward-and-stone/healthcare-advisory" element={<HealthcareAdvisory />} />
          <Route path="/steward-and-stone/operational-strategy" element={<OperationalStrategy />} />
          <Route path="/steward-and-stone/assessment" element={<StewardAssessment />} />

          {/* Strategic Assets — a Jennae Michelle Holdings portfolio company */}
          <Route path="/strategic-assets" element={<StrategicAssets />} />
          <Route path="/strategic-assets/intelligence" element={<SAIntelligence />} />
          <Route path="/strategic-assets/tax-optimization" element={<SATaxOptimization />} />
          <Route path="/strategic-assets/recovery" element={<SARecovery />} />
          <Route path="/strategic-assets/acquisitions" element={<SAAcquisitions />} />
          <Route path="/strategic-assets/real-estate" element={<SARealEstate />} />

          {/* Backward-compatible redirects — no legacy inbound link may 404 */}
          <Route path="/steward-and-stone/strategic-assets" element={<Navigate to="/strategic-assets" replace />} />
          <Route path="/steward-and-stone/tax-optimizer" element={<Navigate to="/strategic-assets/tax-optimization" replace />} />
          <Route path="/steward-and-stone/tax-optimization" element={<Navigate to="/strategic-assets/tax-optimization" replace />} />
          <Route path="/steward-and-stone/wealth" element={<Navigate to="/steward-and-stone" replace />} />
          <Route path="/steward-and-stone/advisory" element={<Navigate to="/steward-and-stone/operational-strategy" replace />} />
          <Route path="/strategic-assets/acquisition" element={<Navigate to="/strategic-assets/acquisitions" replace />} />
          <Route path="/acquisition" element={<Navigate to="/strategic-assets/acquisitions" replace />} />
          <Route path="/recovery" element={<Navigate to="/strategic-assets/recovery" replace />} />

          <Route path="/foundation" element={<Foundation />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/investor-login" element={<InvestorLogin />} />
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route path="/legal" element={<LegalPortal />} />
        <Route path="/login" element={<FounderLogin />} />
        <Route path="/founder" element={<Navigate to="/founder/cfo" replace />} />
        <Route element={<FounderAccess />}>
        <Route path="/founder/website-analytics" element={<WebsiteAnalytics />} />
        <Route path="/founder/profile" element={<FounderProfile />} />
        <Route path="/founder/ownership" element={<FounderOwnership />} />
        <Route path="/founder/operations" element={<FounderOperations />} />
        <Route path="/steward-and-stone/inbox" element={<FounderInbox />} />
        <Route path="/secure-intake" element={<SecureIntake />} />
        <Route path="/founder/close" element={<FounderCloseConsole />} />
        <Route path="/founder/time" element={<FounderTimeLedger />} />
        <Route path="/founder/planning" element={<FounderPlanningConsole />} />
        <Route path="/founder/assets" element={<FounderAssetRegister />} />
        <Route path="/founder/procurement" element={<FounderProcureToPay />} />
        <Route path="/founder/contract-to-cash" element={<FounderContractToCash />} />
        <Route path="/founder/treasury" element={<FounderTreasury />} />
        <Route path="/founder/tax-compliance" element={<FounderTaxCompliance />} />
        <Route path="/founder/governance" element={<FounderGovernance />} />
        <Route path="/founder/cfo" element={<FounderCFO />} />
        <Route path="/founder/hours" element={<FounderHoursSheet />} />
        </Route>
      </Routes></PageErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
